import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, Users, FolderKanban, AlertCircle, Clock, Calendar, CheckCircle2, Bell, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import MyAttendanceWidget from "@/components/MyAttendanceWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const projectData = [
  { month: "Jan", completed: 3, active: 5 },
  { month: "Feb", completed: 5, active: 7 },
  { month: "Mar", completed: 4, active: 6 },
  { month: "Apr", completed: 6, active: 8 },
  { month: "May", completed: 5, active: 9 },
  { month: "Jun", completed: 7, active: 10 },
];

export default function DashboardPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "Admin";

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50 dark:bg-slate-900">
        <div className="text-slate-600 dark:text-slate-400">Loading dashboard...</div>
      </div>
    );
  }

  const moduleCards = [
    {
      title: "Leads",
      count: stats.total_leads,
      newCount: stats.new_leads_today,
      icon: Users,
      bgColor: "bg-gradient-to-br from-brillien-50 to-red-50",
      iconBg: "bg-brillien-600",
      textColor: "text-brillien-700",
      borderColor: "border-brillien-200",
      module: "leads",
      hasUpdates: stats.module_updates?.leads > 0
    },
    {
      title: "Projects",
      count: stats.total_projects,
      newCount: stats.active_projects,
      icon: FolderKanban,
      bgColor: "bg-gradient-to-br from-blue-50 to-indigo-50",
      iconBg: "bg-blue-600",
      textColor: "text-blue-700",
      borderColor: "border-blue-200",
      module: "projects"
    },
    {
      title: "Tasks",
      count: stats.pending_tasks,
      newCount: stats.overdue_tasks,
      icon: CheckCircle2,
      bgColor: "bg-gradient-to-br from-emerald-50 to-teal-50",
      iconBg: "bg-emerald-600",
      textColor: "text-emerald-700",
      borderColor: "border-emerald-200",
      module: "tasks",
      hasUpdates: stats.module_updates?.tasks > 0
    },
    {
      title: "Leave Requests",
      count: stats.pending_leaves,
      newCount: stats.total_employees,
      icon: Calendar,
      bgColor: "bg-gradient-to-br from-amber-50 to-yellow-50",
      iconBg: "bg-amber-600",
      textColor: "text-amber-700",
      borderColor: "border-amber-200",
      module: "team",
      hasUpdates: stats.module_updates?.team > 0
    },
  ];

  return (
    <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
          Welcome back, <span className="text-brillien-600 dark:text-brillien-400">{user?.name}</span>
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">Here's what's happening with your projects today</p>
      </div>

      {/* My Attendance Widget (visible only to employees with an employee record) */}
      <MyAttendanceWidget />

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {moduleCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.title}
              className={`relative p-6 border ${card.borderColor} dark:border-slate-700 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer ${card.bgColor} dark:bg-slate-800 overflow-hidden group`}
              onClick={() => navigate(`/${card.module}`)}
              data-testid={`dashboard-card-${card.module}`}
            >
              {/* Update Badge */}
              {card.hasUpdates && (
                <div className="absolute top-4 right-4">
                  <div className="relative">
                    <Bell className="h-5 w-5 text-brillien-500 animate-pulse" />
                    <span className="absolute -top-1 -right-1 h-3 w-3 bg-brillien-500 rounded-full border-2 border-white dark:border-slate-800" />
                  </div>
                </div>
              )}

              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl ${card.iconBg} shadow-sm`}>
                    <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {card.title}
                  </p>
                  <div className="flex items-end gap-3">
                    <p className="font-mono text-3xl font-bold text-slate-900 dark:text-white">
                      {card.count}
                    </p>
                    {card.newCount > 0 && (
                      <Badge className={`${card.bgColor} ${card.textColor} ${card.borderColor} dark:bg-slate-700 dark:border-slate-400 rounded-full px-2 py-0.5 text-xs font-medium border`}>
                        {card.title === "Leave Requests" ? `${card.newCount} employees` : 
                         card.title === "Leads" ? `+${card.newCount} today` :
                         card.title === "Projects" ? `${card.newCount} active` :
                         card.title === "Tasks" ? `${card.newCount} overdue` : card.newCount}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex items-center text-sm font-medium text-slate-500 dark:text-slate-400 group-hover:text-brillien-600 dark:group-hover:text-brillien-400 transition-colors">
                  View details <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Charts and Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Project Trends */}
        <Card className="lg:col-span-2 p-6 border-slate-200 dark:border-slate-700 rounded-xl shadow-sm bg-white dark:bg-slate-800">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">
                Project Progress
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Monthly completion trends</p>
            </div>
            <Badge variant="outline" className="rounded-full text-xs dark:border-slate-400 dark:text-slate-300">
              Last 6 months
            </Badge>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={projectData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" className="dark:stroke-slate-700" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#64748B" />
              <YAxis tick={{ fontSize: 12 }} stroke="#64748B" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
              />
              <Bar dataKey="completed" fill="#DC2626" radius={[6, 6, 0, 0]} name="Completed" />
              <Bar dataKey="active" fill="#3B82F6" radius={[6, 6, 0, 0]} name="Active" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Quick Stats Summary */}
        <Card className="p-6 border-slate-200 dark:border-slate-700 rounded-xl shadow-sm bg-white dark:bg-slate-800">
          <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white mb-5">
            Quick Stats
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-400">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-brillien-100 dark:bg-brillien-900/50">
                  <Users className="h-4 w-4 text-brillien-600 dark:text-brillien-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Total Vendors</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Active suppliers</p>
                </div>
              </div>
              <p className="font-mono text-xl font-bold text-slate-900 dark:text-white">{stats.total_vendors}</p>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-400">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
                  <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Team Members</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Active employees</p>
                </div>
              </div>
              <p className="font-mono text-xl font-bold text-slate-900 dark:text-white">{stats.total_employees}</p>
            </div>

            {isAdmin && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-brillien-50 dark:bg-brillien-900/30 border border-brillien-200 dark:border-brillien-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-brillien-600">
                    <AlertCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-brillien-900 dark:text-brillien-300">Pending Invoices</p>
                    <p className="text-xs text-brillien-700 dark:text-brillien-400">Needs attention</p>
                  </div>
                </div>
                <p className="font-mono text-xl font-bold text-brillien-600 dark:text-brillien-400">{stats.pending_invoices}</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Recent Activities */}
      <Card className="p-6 border-slate-200 dark:border-slate-700 rounded-xl shadow-sm bg-white dark:bg-slate-800">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">
            Recent Activities
          </h3>
          <Badge variant="outline" className="rounded-full text-xs dark:border-slate-400 dark:text-slate-300">
            Live Updates
          </Badge>
        </div>
        <div className="space-y-3">
          {stats.recent_activities.slice(0, 6).map((activity, idx) => (
            <div
              key={idx}
              className="flex items-start gap-4 p-4 rounded-lg border border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer group"
              onClick={() => navigate(`/${activity.module}`)}
            >
              <div className={`p-2 rounded-lg ${
                activity.type === 'lead' ? 'bg-brillien-100 dark:bg-brillien-900/50' :
                activity.type === 'project' ? 'bg-blue-100 dark:bg-blue-900/50' :
                'bg-emerald-100 dark:bg-emerald-900/50'
              }`}>
                {activity.type === 'lead' ? <Users className="h-4 w-4 text-brillien-600 dark:text-brillien-400" /> :
                 activity.type === 'project' ? <FolderKanban className="h-4 w-4 text-blue-600 dark:text-blue-400" /> :
                 <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900 dark:text-white group-hover:text-brillien-700 dark:group-hover:text-brillien-400">
                  {activity.message}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {new Date(activity.time).toLocaleString()}
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 dark:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>
      </Card>

      {/* Pending Actions for Admin */}
      {isAdmin && (stats.pending_leaves > 0 || stats.overdue_tasks > 0) && (
        <Card className="mt-6 p-6 border-2 border-brillien-200 dark:border-brillien-800 rounded-xl shadow-sm bg-brillien-50 dark:bg-brillien-900/30">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-brillien-600">
              <AlertCircle className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold text-brillien-900 dark:text-brillien-300 mb-2">
                Action Required
              </h3>
              <div className="space-y-2">
                {stats.pending_leaves > 0 && (
                  <p className="text-sm text-brillien-800 dark:text-brillien-400">
                    • <strong>{stats.pending_leaves}</strong> leave request{stats.pending_leaves > 1 ? 's' : ''} pending approval
                  </p>
                )}
                {stats.overdue_tasks > 0 && (
                  <p className="text-sm text-brillien-800 dark:text-brillien-400">
                    • <strong>{stats.overdue_tasks}</strong> overdue task{stats.overdue_tasks > 1 ? 's' : ''} need attention
                  </p>
                )}
              </div>
              <div className="flex gap-3 mt-4">
                {stats.pending_leaves > 0 && (
                  <Button
                    size="sm"
                    onClick={() => navigate('/team')}
                    className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg"
                  >
                    Review Leaves
                  </Button>
                )}
                {stats.overdue_tasks > 0 && (
                  <Button
                    size="sm"
                    onClick={() => navigate('/tasks')}
                    variant="outline"
                    className="border-brillien-300 dark:border-brillien-700 text-brillien-700 dark:text-brillien-400 hover:bg-brillien-100 dark:hover:bg-brillien-900/50 rounded-lg"
                  >
                    View Tasks
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
